import { IS_RENDER_COUNT_ENABLED } from '../../../helpers/renderCountDebug';

// React.memo comparator for panels rendered inside a react-final-form <Form render={...}>.
//
// 1. The `form` that react-final-form's render callback hands out is a brand-new shallow copy on every
//    render (it is `{ ...form, reset }`), so a plain shallow comparison never sees it as unchanged and a
//    memoized panel would re-render every time its parent does. Every copy wraps the same underlying
//    form (same methods, same state), so for rendering purposes `form` is never "different" - it is
//    ignored here. (Only the `useForm()` context value is a single, stable object.)
//
// 2. Panels that read several form values get them as one `formValues` object (rebuilt by the panel's
//    FormSpy on every form change). It is compared one level deep - its picked values - so the panel
//    only re-renders when a value it actually displays changes.
//
// Every other prop is compared by reference.
const isShallowEqual = (previous: object, next: object): boolean => {
    const previousKeys = Object.keys(previous);
    const nextKeys = Object.keys(next);
    if (previousKeys.length !== nextKeys.length) {
        return false;
    }
    return previousKeys.every((key) => (previous as Record<string, unknown>)[key] === (next as Record<string, unknown>)[key]);
};

// DEBUG (render-count flag only): says which prop - or which picked form value - made a memoised panel
// re-render. Never runs when the flag is off.
const logChangedProps = (previous: object, next: object): void => {
    const changed: string[] = [];
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
    keys.forEach((key) => {
        if (key === 'form') {
            return;
        }
        const previousValue = (previous as Record<string, unknown>)[key];
        const nextValue = (next as Record<string, unknown>)[key];
        if (key === 'formValues' && previousValue && nextValue && typeof previousValue === 'object' && typeof nextValue === 'object') {
            Object.keys({ ...previousValue, ...nextValue }).forEach((valueKey) => {
                if ((previousValue as Record<string, unknown>)[valueKey] !== (nextValue as Record<string, unknown>)[valueKey]) {
                    changed.push('formValues.' + valueKey);
                }
            });
        } else if (previousValue !== nextValue) {
            changed.push(key);
        }
    });
    if (changed.length) {
        // tslint:disable-next-line: no-console
        console.log('[render-count] panel re-rendered because:', changed.join(', '));
    }
};

export const arePropsEqualIgnoringForm = <Props extends { form: unknown }>(previous: Props, next: Props): boolean => {
    if (IS_RENDER_COUNT_ENABLED) {
        logChangedProps(previous, next);
    }
    const previousKeys = Object.keys(previous) as Array<keyof Props>;
    const nextKeys = Object.keys(next) as Array<keyof Props>;
    if (previousKeys.length !== nextKeys.length) {
        return false;
    }
    return previousKeys.every((key) => {
        if (key === 'form') {
            return true;
        }
        const previousValue = previous[key];
        const nextValue = next[key];
        if (key === 'formValues' && previousValue && nextValue && typeof previousValue === 'object' && typeof nextValue === 'object') {
            return isShallowEqual(previousValue as object, nextValue as object);
        }
        return previousValue === nextValue;
    });
};
